const API_BASE_URL = 'https://client-api.fluxuro.ai';

function getParamValue(param) {
  const key = param.parameter_name || param.parameter_title;
  if (!key) return undefined;

  // Use default_value if available
  if (param.default_value != null && param.default_value !== '') {
    return param.default_value;
  }

  // Use first allowed value if available
  if (param.allowed_values) {
    try {
      const allowed = typeof param.allowed_values === 'string'
        ? JSON.parse(param.allowed_values)
        : param.allowed_values;
      if (Array.isArray(allowed) && allowed.length > 0) {
        return allowed[0];
      }
    } catch { /* ignore */ }
  }

  // Type-based fallbacks. Media params get an example URL matching their KIND —
  // handing a customer 'your-image.jpg' for reference_audio_urls sends them
  // straight into a provider rejection.
  const mediaExample = (name) => {
    const n = String(name || '').toLowerCase();
    if (n.includes('video')) return 'https://example.com/your-video.mp4';
    if (n.includes('audio')) return 'https://example.com/your-audio.mp3';
    return 'https://example.com/your-image.jpg';
  };

  if (param.data_type === 'image' || param.data_type === 'image_url') {
    return mediaExample(key);
  }
  if (param.data_type === 'object[]') {
    return [mediaExample(key)];
  }
  if (param.data_type === 'number' || param.data_type === 'integer' || param.data_type === 'float') {
    return param.allowed_min_value != null ? param.allowed_min_value : null;
  }

  return `YOUR_${key.toUpperCase()}`;
}

// --- Model helpers ---

export function buildModelRequestBody(model) {
  const body = { model_id: model.model_id };
  if (model.parameters && model.parameters.length > 0) {
    model.parameters.forEach((param) => {
      const key = param.parameter_name || param.parameter_title;
      if (!key) return;
      body[key] = getParamValue(param);
    });
  }
  body.webhook_url = 'https://your-webhook-url.com/callback';
  return body;
}

export function generateModelCurl(model) {
  const body = buildModelRequestBody(model);
  let curl = `curl --location '${API_BASE_URL}/api/models/run-model'`;
  curl += ` \\\n  --header 'x-api-key: YOUR_API_KEY'`;
  curl += ` \\\n  --header 'Content-Type: application/json'`;
  curl += ` \\\n  --data '${JSON.stringify(body, null, 2)}'`;
  return curl;
}

export function generateModelAxios(model) {
  const body = buildModelRequestBody(model);
  const bodyStr = JSON.stringify(body, null, 2).split('\n').join('\n  ');
  return `import axios from 'axios';

const response = await axios.post(
  '${API_BASE_URL}/api/models/run-model',
  ${bodyStr},
  {
    headers: {
      'x-api-key': 'YOUR_API_KEY',
      'Content-Type': 'application/json',
    },
  }
);

console.log(response.data);
// response.data.data.image_job_id -> use this to check job status`;
}

export function generateModelPython(model) {
  const body = buildModelRequestBody(model);
  const bodyStr = JSON.stringify(body, null, 4)
    .replace(/true/g, 'True')
    .replace(/false/g, 'False')
    .replace(/null/g, 'None');
  return `import requests

url = "${API_BASE_URL}/api/models/run-model"

headers = {
    "x-api-key": "YOUR_API_KEY",
    "Content-Type": "application/json",
}

data = ${bodyStr}

response = requests.post(url, headers=headers, json=data)
print(response.json())
# response.json()["data"]["image_job_id"] -> use this to check job status`;
}

// --- Workflow helpers ---

export function buildWorkflowRequestBody(workflow) {
  const inputParams = {};
  if (workflow.parameters && workflow.parameters.length > 0) {
    workflow.parameters.forEach((param) => {
      const key = param.parameter_name || param.parameter_title;
      if (!key) return;
      inputParams[key] = getParamValue(param);
    });
  }
  return {
    workflow_definition_id: workflow.workflow_definition_id,
    input_params: inputParams,
    webhook_url: 'https://your-webhook-url.com/callback',
  };
}

export function generateWorkflowCurl(workflow) {
  const body = buildWorkflowRequestBody(workflow);
  let curl = `curl --location '${API_BASE_URL}/api/workflows/run-workflow'`;
  curl += ` \\\n  --header 'x-api-key: YOUR_API_KEY'`;
  curl += ` \\\n  --header 'Content-Type: application/json'`;
  curl += ` \\\n  --data '${JSON.stringify(body, null, 2)}'`;
  return curl;
}

export function generateWorkflowAxios(workflow) {
  const body = buildWorkflowRequestBody(workflow);
  const bodyStr = JSON.stringify(body, null, 2).split('\n').join('\n  ');
  return `import axios from 'axios';

const response = await axios.post(
  '${API_BASE_URL}/api/workflows/run-workflow',
  ${bodyStr},
  {
    headers: {
      'x-api-key': 'YOUR_API_KEY',
      'Content-Type': 'application/json',
    },
  }
);

console.log(response.data);
// response.data.data.workflow_job_id -> use this to check job status`;
}

export function generateWorkflowPython(workflow) {
  const body = buildWorkflowRequestBody(workflow);
  const bodyStr = JSON.stringify(body, null, 4)
    .replace(/true/g, 'True')
    .replace(/false/g, 'False')
    .replace(/null/g, 'None');
  return `import requests

url = "${API_BASE_URL}/api/workflows/run-workflow"

headers = {
    "x-api-key": "YOUR_API_KEY",
    "Content-Type": "application/json",
}

data = ${bodyStr}

response = requests.post(url, headers=headers, json=data)
print(response.json())
# response.json()["data"]["workflow_job_id"] -> use this to check job status`;
}

// --- Check job status helpers ---

export function generateCheckJobCurl(jobId = 'YOUR_IMAGE_JOB_ID') {
  return `curl --location '${API_BASE_URL}/api/image-jobs/image-job/${jobId}' \\
  --header 'x-api-key: YOUR_API_KEY'`;
}

export function generateCheckJobAxios(jobId = 'YOUR_IMAGE_JOB_ID') {
  return `import axios from 'axios';

const response = await axios.get(
  '${API_BASE_URL}/api/image-jobs/image-job/${jobId}',
  {
    headers: {
      'x-api-key': 'YOUR_API_KEY',
    },
  }
);

console.log(response.data);`;
}

export function generateCheckJobPython(jobId = 'YOUR_IMAGE_JOB_ID') {
  return `import requests

url = "${API_BASE_URL}/api/image-jobs/image-job/${jobId}"

headers = {
    "x-api-key": "YOUR_API_KEY",
}

response = requests.get(url, headers=headers)
print(response.json())`;
}

export function generateCheckWorkflowJobCurl(jobId = 'YOUR_WORKFLOW_JOB_ID') {
  return `curl --location '${API_BASE_URL}/api/workflows/workflow-jobs/workflow-job/${jobId}' \\
  --header 'x-api-key: YOUR_API_KEY'`;
}

export function generateCheckWorkflowJobAxios(jobId = 'YOUR_WORKFLOW_JOB_ID') {
  return `import axios from 'axios';

const response = await axios.get(
  '${API_BASE_URL}/api/workflows/workflow-jobs/workflow-job/${jobId}',
  {
    headers: {
      'x-api-key': 'YOUR_API_KEY',
    },
  }
);

console.log(response.data);`;
}

export function generateCheckWorkflowJobPython(jobId = 'YOUR_WORKFLOW_JOB_ID') {
  return `import requests

url = "${API_BASE_URL}/api/workflows/workflow-jobs/workflow-job/${jobId}"

headers = {
    "x-api-key": "YOUR_API_KEY",
}

response = requests.get(url, headers=headers)
print(response.json())`;
}
